import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { HomePage } from '@/pages/HomePage'
import { SignInPage } from '@/pages/SignInPage'
import { SignUpPage } from '@/pages/SignUpPage'
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RoleGate } from '@/components/shell/RoleGate'
import { NotificationProvider } from '@/lib/notifications'
import { TranslationJobProvider } from '@/lib/translationJobs'
import { useSyncUser } from '@/lib/syncUser'
import { FEATURE_ROLE_PLAY_ROUTES_ENABLED, FEATURE_OPENAI_REALTIME, FEATURE_ORGANISATIONS } from '@/lib/featureFlags'
import { EditorGate } from '@/components/shell/EditorGate'
import { OrgProvider } from '@/components/shell/OrgProvider'
import { AppShell } from '@/components/shell/AppShell'
import { ClerkNavigationBridge } from '@/components/ClerkNavigationBridge'

// Lazy-loaded route components. These pages are heavy (the Dialogue
// play page pulls in lottie-react, the Anthropic SDK, our STT lib,
// and the entire ConversationStage; the editor pulls in pdfjs and
// the full slide library). Loading them eagerly bloats the home-page
// chunk to ~2.7MB. With React.lazy each one ships in its own JS
// chunk, so navigating to / first loads only the HomePage shell
// and signed-in chrome; the other routes' code is fetched on demand
// when the user navigates there.
const LibraryPage         = lazy(() => import('@/pages/LibraryPage').then(m => ({ default: m.LibraryPage })))
const EditorPage          = lazy(() => import('@/pages/EditorPage').then(m => ({ default: m.EditorPage })))
const JoinPage            = lazy(() => import('@/pages/JoinPage').then(m => ({ default: m.JoinPage })))
const CourseWizardPage    = lazy(() => import('@/pages/CourseWizardPage').then(m => ({ default: m.CourseWizardPage })))
const ResumePage          = lazy(() => import('@/pages/ResumePage').then(m => ({ default: m.ResumePage })))
// The first-run flow. This is where new accounts land (see ProtectedRoute) and
// where a fresh sign-up is redirected.
const TranslatePage       = lazy(() => import('@/pages/TranslatePage').then(m => ({ default: m.TranslatePage })))
const CourseTemplatesPage = lazy(() => import('@/pages/CourseTemplatesPage').then(m => ({ default: m.CourseTemplatesPage })))
const LibraryDetailPage   = lazy(() => import('@/pages/LibraryDetailPage').then(m => ({ default: m.LibraryDetailPage })))
const LibraryCatalogPage  = lazy(() => import('@/pages/LibraryCatalogPage').then(m => ({ default: m.LibraryCatalogPage })))
const MembersPage         = lazy(() => import('@/pages/org/MembersPage'))
const InvitePage          = lazy(() => import('@/pages/org/InvitePage'))
const OrgSettingsPage     = lazy(() => import('@/pages/org/SettingsPage'))
const AttributesPage      = lazy(() => import('@/pages/org/AttributesPage'))
const AttributeEditPage   = lazy(() => import('@/pages/org/AttributeEditPage'))
const GroupEditPage       = lazy(() => import('@/pages/org/GroupEditPage'))
const UserGroupsPage      = lazy(() => import('@/pages/org/UserGroupsPage'))
const ReportsPage         = lazy(() => import('@/pages/org/ReportsPage'))
const LaunchPage          = lazy(() => import('@/pages/LaunchPage'))
const MyLearningPage      = lazy(() => import('@/pages/MyLearningPage'))
const LearnPage           = lazy(() => import('@/pages/LearnPage'))
const MyDetailsPage       = lazy(() => import('@/pages/org/MyDetailsPage'))
const OrgInviteLinkPage   = lazy(() => import('@/pages/OrgInviteLinkPage'))
const OrgCodeLinkPage     = lazy(() => import('@/pages/OrgCodeLinkPage'))
const OrgSetupPage        = lazy(() => import('@/pages/OrgSetupPage'))
const OrgInvitedPage      = lazy(() => import('@/pages/OrgInvitedPage'))
const LibraryCollectionPage = lazy(() => import('@/pages/LibraryCollectionPage').then(m => ({ default: m.LibraryCollectionPage })))
const PlayerPage          = lazy(() => import('@/pages/PlayerPage').then(m => ({ default: m.PlayerPage })))
const PlayPage            = lazy(() => import('@/pages/PlayPage').then(m => ({ default: m.PlayPage })))
// Role Play (codename: Dialogue) — separate product surface, see CLAUDE.md.
// User-facing routes: /roleplay/* (admin) and /play-roleplay/:code (learner).
// Internal file names still use "Dialogue" — intentional brand vs codename split.
const DialogueListPage    = lazy(() => import('@/pages/DialogueListPage').then(m => ({ default: m.DialogueListPage })))
const DialogueWizardPage  = lazy(() => import('@/pages/DialogueWizardPage').then(m => ({ default: m.DialogueWizardPage })))
const DialogueSessionsPage = lazy(() => import('@/pages/DialogueSessionsPage').then(m => ({ default: m.DialogueSessionsPage })))
const DialoguePlayPage    = lazy(() => import('@/pages/DialoguePlayPage').then(m => ({ default: m.DialoguePlayPage })))
const ProcessLibraryPage  = lazy(() => import('@/pages/ProcessLibraryPage').then(m => ({ default: m.ProcessLibraryPage })))
// M2 STT harness — internal validation surface for Architecture C STT layer.
// Gated by FEATURE_OPENAI_REALTIME so it doesn't appear on prod.
const SttHarnessPage      = lazy(() => import('@/pages/SttHarnessPage').then(m => ({ default: m.SttHarnessPage })))
// M3 TTS harness — tone-variance validation for Architecture C TTS layer.
const TtsHarnessPage      = lazy(() => import('@/pages/TtsHarnessPage').then(m => ({ default: m.TtsHarnessPage })))
// Translation harness — Sarvam vs OpenAI Tamil quality comparison (2026-06-29).
// Decides whether to swap the customer-brain model from gpt-4.1-mini → Sarvam-M.
const TranslationHarnessPage = lazy(() => import('@/pages/TranslationHarnessPage').then(m => ({ default: m.TranslationHarnessPage })))
// Tamil TTS compare harness — Sarvam Bulbul vs OpenAI vs ElevenLabs (2026-06-29).
// Picks the streaming TTS replacement for Bulbul on the ta-hybrid voice path.
const TtsCompareHarnessPage = lazy(() => import('@/pages/TtsCompareHarnessPage').then(m => ({ default: m.TtsCompareHarnessPage })))

/**
 * Page-level loading fallback shown while a lazy route's chunk is
 * being fetched. Plain centred spinner — keeps the visual cue
 * minimal so the chunk-fetch latency reads as "loading" not "broken".
 */
function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
    </div>
  )
}

/**
 * One editor per course (21 Oct 2026). Switching language version from the editor's Translate
 * menu only changes :id, so React kept the same EditorPage — its state (modules, share code) was
 * the previous version's, and a publish or autosave wrote it to the other one: Greeting Customers'
 * Hindi version published under English's code, and English lost its slides. A key per course
 * makes each version a fresh editor.
 */
function EditorForCourse() {
  const { id } = useParams()
  return <EditorPage key={id} />
}

export default function App() {
  // Keep the signed-in user's app_users row current (owner names/avatars). Guarded by a
  // localStorage marker so it hits the DB ~once per user per device per week, not per load.
  useSyncUser()


  return (
    <BrowserRouter>
      <ClerkNavigationBridge />
      <NotificationProvider>
      <TranslationJobProvider>
      <OrgProvider>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/player/:courseId" element={<PlayerPage />} />
          <Route path="/play/:shareCode" element={<PlayPage />} />
          {/* Role Play learner route — silently redirect on production
             until Role Play is ready. See featureFlags.ts. */}
          <Route
            path="/play-roleplay/:code"
            element={FEATURE_ROLE_PLAY_ROUTES_ENABLED
              ? <DialoguePlayPage />
              : <Navigate to="/" replace />}
          />

          {/* Protected routes — must be signed in */}
          {/* Organisations (dev only): the same pages inside the app shell — header with
             the organisation pill, role-based sidebar. The editor, wizard, Translate and
             players stay outside it (focused, full-screen tasks). Production keeps the
             routes exactly as they were (the branch below). */}
          {FEATURE_ORGANISATIONS ? (
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/" element={<HomePage />} />
              <Route path="/courses" element={<RoleGate need="creator" otherwise={<MyLearningPage />}><LibraryPage /></RoleGate>} />
              <Route path="/courses/:rootId" element={<RoleGate need="creator"><LibraryDetailPage /></RoleGate>} />
              <Route path="/courses/:rootId/launch" element={<RoleGate need="manager"><LaunchPage /></RoleGate>} />
              <Route path="/templates" element={<RoleGate need="creator"><CourseTemplatesPage /></RoleGate>} />
              <Route path="/library" element={<LibraryCatalogPage />} />
              <Route path="/library/:libraryId" element={<LibraryCollectionPage />} />
              <Route path="/org/members" element={<RoleGate need="manager"><MembersPage /></RoleGate>} />
              <Route path="/org/invite" element={<RoleGate need="manager"><InvitePage /></RoleGate>} />
              <Route path="/org/attributes" element={<RoleGate need="manager"><AttributesPage /></RoleGate>} />
              <Route path="/org/attributes/groups/:id" element={<RoleGate need="manager"><GroupEditPage kind="auto" /></RoleGate>} />
              <Route path="/org/attributes/:key" element={<RoleGate need="manager"><AttributeEditPage /></RoleGate>} />
              <Route path="/org/groups" element={<RoleGate need="manager"><UserGroupsPage /></RoleGate>} />
              <Route path="/me/organisation" element={<MyDetailsPage />} />
              <Route path="/org/groups/:id" element={<RoleGate need="manager"><GroupEditPage kind="manual" /></RoleGate>} />
              <Route path="/org/settings" element={<RoleGate need="manager"><OrgSettingsPage /></RoleGate>} />
              <Route path="/org/reports" element={<RoleGate need="manager"><ReportsPage /></RoleGate>} />
              {/* My learning is a tab of Courses now (12 Oct 2026); the old address still works. */}
              <Route path="/learning" element={<Navigate to="/courses?tab=learning" replace />} />
              {/* An assigned course: the learner's course page in the shell; the player opens full screen over it. */}
              <Route path="/learn/:launchId" element={<LearnPage />} />
            </Route>
          ) : (
            <>
          <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          {/* `/courses` is the full course list (see all), shown as
             translation-family decks. `/library` used to alias `/courses` —
             it now points at the super-user curated catalog instead, so any
             pre-existing `/library/:rootId` bookmarks (translation-family
             detail) will 404; the equivalent page moved to `/courses/:rootId`. */}
          <Route path="/courses" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
          <Route path="/courses/:rootId" element={<ProtectedRoute><LibraryDetailPage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><CourseTemplatesPage /></ProtectedRoute>} />
          {/* Curated Library: collections list → one collection's courses → one course preview/clone. */}
          <Route path="/library" element={<ProtectedRoute><LibraryCatalogPage /></ProtectedRoute>} />
          <Route path="/library/:libraryId" element={<ProtectedRoute><LibraryCollectionPage /></ProtectedRoute>} />
            </>
          )}
          {/* Public on purpose: the whole point is that a visitor gets all the
             way to a built course before being asked who they are. */}
          {/* Where an OAuth provider returns to. The journey sends people
             straight to Google rather than opening Clerk's form on top of its
             own, and this is the landing strip for that round trip. */}
          {/* /journey was removed (a962fbf); a Google sign-in that lands here goes Home, where the app shell takes over. */}
          <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback signUpForceRedirectUrl="/" signInForceRedirectUrl="/" />} />
          {/* Where the marketing site's handoff lands, after Clerk. Protected:
             claiming a draft attaches it to an account, so there has to be one. */}
          <Route path="/resume" element={<ProtectedRoute><ResumePage /></ProtectedRoute>} />
          <Route path="/course/new" element={<ProtectedRoute><RoleGate need="creator"><CourseWizardPage /></RoleGate></ProtectedRoute>} />
          <Route path="/course/:id/translate" element={<ProtectedRoute><TranslatePage /></ProtectedRoute>} />
          <Route path="/editor/:id" element={<ProtectedRoute><EditorGate><EditorForCourse /></EditorGate></ProtectedRoute>} />
          {/* Organisations (dev only): an invitation email's link, and an organisation
             code's join link / QR. Public — the person may not have an account yet.
             Not /join/:token, which is a course share link. */}
          {FEATURE_ORGANISATIONS && <Route path="/join/org/:token" element={<OrgInviteLinkPage />} />}
          {FEATURE_ORGANISATIONS && <Route path="/o/:code" element={<OrgCodeLinkPage />} />}
          {/* Organisation setup — focused pages, no sidebar: right after sign-up, and "Create organisation". */}
          {FEATURE_ORGANISATIONS && <Route path="/org/setup" element={<ProtectedRoute><OrgSetupPage mode="setup" /></ProtectedRoute>} />}
          {FEATURE_ORGANISATIONS && <Route path="/org/new" element={<ProtectedRoute><OrgSetupPage mode="create" /></ProtectedRoute>} />}
          {FEATURE_ORGANISATIONS && <Route path="/org/invited" element={<ProtectedRoute><OrgInvitedPage /></ProtectedRoute>} />}
          {/* Redeem a course share link → adds the signed-in user, then redirects into the course */}
          <Route path="/join/:token" element={<ProtectedRoute><JoinPage /></ProtectedRoute>} />

          {/* Role Play admin routes — silently redirect to / on
             production until Role Play is ready (see featureFlags.ts).
             Mounted as-normal on dev so the internal team can reach
             them via direct URL. */}
          {FEATURE_ROLE_PLAY_ROUTES_ENABLED ? (
            <>
              <Route path="/roleplays" element={<ProtectedRoute><DialogueListPage /></ProtectedRoute>} />
              <Route path="/roleplay/new" element={<ProtectedRoute><DialogueWizardPage /></ProtectedRoute>} />
              <Route path="/roleplay/:id/edit" element={<ProtectedRoute><DialogueWizardPage /></ProtectedRoute>} />
              <Route path="/roleplay/:id/sessions" element={<ProtectedRoute><DialogueSessionsPage /></ProtectedRoute>} />
              <Route path="/processes" element={<ProtectedRoute><ProcessLibraryPage /></ProtectedRoute>} />
            </>
          ) : (
            <>
              <Route path="/roleplays" element={<Navigate to="/" replace />} />
              <Route path="/roleplay/new" element={<Navigate to="/" replace />} />
              <Route path="/roleplay/:id/edit" element={<Navigate to="/" replace />} />
              <Route path="/roleplay/:id/sessions" element={<Navigate to="/" replace />} />
              <Route path="/processes" element={<Navigate to="/" replace />} />
            </>
          )}

          {/* M2 + M3 internal harnesses — not in nav, gated to non-prod */}
          {FEATURE_OPENAI_REALTIME && (
            <>
              <Route path="/role-play-stt-harness" element={<ProtectedRoute><SttHarnessPage /></ProtectedRoute>} />
              <Route path="/role-play-tts-harness" element={<ProtectedRoute><TtsHarnessPage /></ProtectedRoute>} />
              <Route path="/role-play-tts-compare" element={<ProtectedRoute><TtsCompareHarnessPage /></ProtectedRoute>} />
              <Route path="/role-play-translation-harness" element={<ProtectedRoute><TranslationHarnessPage /></ProtectedRoute>} />
            </>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </OrgProvider>
      </TranslationJobProvider>
      </NotificationProvider>
    </BrowserRouter>
  )
}
